import React from 'react';

import {
  Box,
  Button,
  Checkbox,
  Divider,
  Drawer,
  FormControl,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  SelectChangeEvent,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import { DisplayFilters } from 'src/utils/filters/display-filters';
import { toggleChefName } from 'src/utils/filters/selected-filters';
import StyledFilledSelect from './StyledFilledSelect';
import useNonNullContext from 'src/hooks/useNonNullContext';
import ConfigContext from '../../context/config';

interface Props {
  displayFilters: DisplayFilters;
  selectedFilters: DisplayFilters;
  setSelectedFilters: React.Dispatch<React.SetStateAction<DisplayFilters>>;
}

const ChefFilter: React.FC<Props> = ({
  displayFilters,
  selectedFilters,
  setSelectedFilters,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('md'),
  );

  const tenantConfig = useNonNullContext(ConfigContext);

  const [dropdownIsOpen, setDropdownIsOpen] = React.useState(false);

  if (!tenantConfig?.mealDetailProfile?.showSupplier) {
    // Do not display the filter if the tenant does not expose chef names on meal cards
    return null;
  }

  const chefsToDisplay = displayFilters.chefNames || [];
  if (chefsToDisplay.length < 2) {
    // Do not display the filter if there are not multiple chefs to choose from
    return null;
  }

  const selectedChefs = selectedFilters.chefNames || [];

  const openDropdown = (e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDropdownIsOpen(true);
  };
  const closeDropdown = () => {
    setDropdownIsOpen(false);
  };

  const resetSelectedChefs = () => {
    setSelectedFilters((prevState) => ({
      ...prevState,
      chefNames: [],
    }));
  };

  const toggleSelectedChef = (chefName: string) => {
    setSelectedFilters((prevState) =>
      toggleChefName({
        currentFilters: prevState,
        chefName,
      }),
    );
  };

  const handleSelectedChefsChange = (event: SelectChangeEvent<unknown>) => {
    setSelectedFilters((prevState) => {
      return {
        ...prevState,
        chefNames: event.target.value as string[],
      };
    });
  };

  const label = 'Chef';

  return (
    <React.Fragment>
      <FormControl sx={{ width: 'auto' }}>
        <StyledFilledSelect
          labelId="chef-select-label"
          displayEmpty
          id="chef-select"
          multiple
          // When on mobile, prevent the Select from opening its Menu and use Drawer instead
          open={!isSmallScreen && dropdownIsOpen}
          onOpen={openDropdown}
          onClose={closeDropdown}
          value={selectedChefs}
          label={label}
          renderValue={() => {
            if (!selectedChefs.length) {
              return <>{label}</>;
            }

            if (selectedChefs.length === 1) {
              return (
                <div css={{ color: theme.palette.primary.contrastText }}>
                  {selectedChefs[0]}
                </div>
              );
            }

            return (
              <div css={{ display: 'flex' }}>
                <div
                  css={{
                    color: theme.palette.primary.contrastText,
                    marginRight: '4px',
                  }}
                >
                  {selectedChefs[0]}
                </div>
                <div css={{ color: theme.palette.secondary.contrastText }}>
                  + {selectedChefs.length - 1} more
                </div>
              </div>
            );
          }}
          onChange={handleSelectedChefsChange}
        >
          {/* Desktop/tablet dropdown items */}
          {chefsToDisplay.map((chef) => (
            <MenuItem
              key={chef}
              disableRipple
              value={chef}
              sx={{
                padding: '0 9px',
                '&.Mui-selected, &.Mui-selected.Mui-focusVisible': {
                  backgroundColor: 'transparent',
                },
                '&.Mui-selected:hover, &.Mui-selected:hover.Mui-selected.Mui-focusVisible':
                  {
                    backgroundColor: theme.palette.action.hover,
                  },
              }}
            >
              <Checkbox
                disableRipple
                sx={{ paddingLeft: 0 }}
                checked={selectedChefs.includes(chef)}
              />
              <ListItemText
                primary={chef}
                slotProps={{ primary: { sx: { fontSize: '0.875rem' } } }}
              />
            </MenuItem>
          ))}
          <div css={{ padding: '9px' }}>
            <Button variant="outlined" onClick={resetSelectedChefs}>
              Reset
            </Button>
          </div>
        </StyledFilledSelect>
      </FormControl>

      {/* Mobile Drawer: acts as the Select menu on small screens */}
      <Drawer
        anchor="bottom"
        open={isSmallScreen && dropdownIsOpen}
        onClose={closeDropdown}
        ModalProps={{ keepMounted: true }}
        css={{
          zIndex: 2000,
        }}
        slotProps={{
          paper: {
            style: { height: 'auto', maxHeight: '85%' },
            sx: {
              borderTopLeftRadius: 10,
              borderTopRightRadius: 10,
            },
          },
        }}
      >
        <Box sx={{ p: '16px 24px' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {label}
          </Typography>
        </Box>
        <Box sx={{ px: 1, py: 0.5 }}>
          <List disablePadding sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {chefsToDisplay.map((chef) => {
              const checked = selectedChefs.includes(chef);
              return (
                <ListItemButton
                  key={chef}
                  onClick={() => toggleSelectedChef(chef)}
                  dense
                  sx={{ py: 0.5 }}
                >
                  <Checkbox disableRipple edge="start" checked={checked} />
                  <ListItemText
                    primary={chef}
                    slotProps={{ primary: { sx: { fontSize: '0.875rem' } } }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Box>
        <Divider />
        <Box
          sx={{
            p: 2,
            display: 'flex',
            gap: 1,
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Button variant="outlined" onClick={resetSelectedChefs}>
            Reset
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" onClick={closeDropdown}>
              Done
            </Button>
          </Box>
        </Box>
      </Drawer>
    </React.Fragment>
  );
};

export default ChefFilter;
